/**
 * EQUALS (banking onboarding) backend error-code mappings.
 *
 * These error codes may occur on each of the listed endpoints.
 * Scope is per endpoint to avoid collisions for generic codes like NOT_FOUND.
 */

export const DEFAULT_ERROR_MESSAGES = {
  en: 'An error occurred. Please try again later.',
  fr: "Une erreur s'est produite. Veuillez réessayer ultérieurement",
};

export const EQUALS_ENDPOINTS = [
  // Matches how FE calls `callAPI(method, api, ...)` today (see `src/store/eBanking.js`)
  'equals/create-associated-people',
  'equals/create-business-application',
  'equals/uploadApplicationDocument',
];

/**
 * Map: error_code -> localized messages.
 *
 * Note: HTTP status is intentionally not stored here; keep mapping driven by
 * `error_code` and rely on `status` separately (logging/analytics).
 */
export const EQUALS_ERROR_MESSAGES_BY_CODE = {
  NOT_FOUND: {
    en: 'Administrative People not found',
    fr: 'Personnel administratif introuvable',
  },
  MISSING_FIELDS: {
    en: 'Missing required fields',
    fr: 'Champs obligatoires manquants',
  },
  MISSING_EMAIL: {
    en: 'Email address not found for administrative people',
    fr: 'Adresse e-mail introuvable pour le personnel administratif',
  },
  RESPONSE_NOT_FOUND: {
    en: 'Financial Partner Application creation failed',
    fr: 'Échec de la création de la demande partenaire financier',
  },
  FINANCIAL_APPLICATION_NOT_FOUND: {
    en: 'Financial application not found',
    fr: 'Demande financière introuvable',
  },
  DOCUMENT_VALIDATION_FAILED: {
    en: 'Document validation failed',
    fr: 'La validation du document a échoué',
  },
  DOCUMENT_UPLOAD_FAILED: {
    en: 'Document upload failed',
    fr: "L'envoi du document a échoué",
  },
  DOCUMENTS_NOT_FOUND: {
    en: 'No documents found to validate',
    fr: 'Aucun document trouvé à valider',
  },
};

/**
 * Map: endpoint -> error_code -> localized messages.
 *
 * All listed EQUALS endpoints share the same codes by default.
 * If later you need endpoint-specific wording, override by editing the endpoint entry.
 */
export const EQUALS_ERROR_MESSAGES_BY_ENDPOINT = EQUALS_ENDPOINTS.reduce(
  (acc, endpoint) => {
    acc[endpoint] = EQUALS_ERROR_MESSAGES_BY_CODE;
    return acc;
  },
  {},
);

function normalizeEndpointKey(endpoint) {
  if (!endpoint) return endpoint;
  // Accept variants like `/api/equals/...` or `/v2/equals/...` and normalize to `equals/...`
  return String(endpoint)
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/+/, '')
    .replace(/^(api\/)?v?\d+\//i, '') // `v2/` or `api/v2/` (best-effort)
    .replace(/^api\//i, '')
    .replace(/^v2\//i, '');
}

/**
 * Resolve a user-facing message for an endpoint + backend error payload.
 * Safe to call from a global API wrapper (interceptor-style).
 */
export function resolveEqualsErrorMessage({
  endpoint,
  error,
  locale = 'fr',
} = {}) {
  const code = error?.error_code || error?.code || error?.errorCode;
  const normalizedLocale = locale === 'en' ? 'en' : 'fr';
  if (!code) return DEFAULT_ERROR_MESSAGES[normalizedLocale];

  const key = normalizeEndpointKey(endpoint);
  const perEndpoint = EQUALS_ERROR_MESSAGES_BY_ENDPOINT?.[key];
  const perCode = perEndpoint?.[code] || EQUALS_ERROR_MESSAGES_BY_CODE?.[code];
  return (
    perCode?.[normalizedLocale] || DEFAULT_ERROR_MESSAGES[normalizedLocale]
  );
}
